(function () {
  'use strict';
  function list(value) {
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()) : [];
  }
  /* A vehicle is a saved-vehicle object ({key, fitmentKey, series}) or a plain fitment key string. A list matches on
     either the exact finder key ("FG Sedan") or the grouped fitment key ("FG–FGX Sedan"). */
  function keysOf(vehicle) {
    if (!vehicle) return [];
    if (typeof vehicle === 'string') return [vehicle];
    return [vehicle.key, vehicle.fitmentKey].filter((key) => typeof key === 'string' && key);
  }
  function compatible(product, variant, vehicle) {
    const keys = keysOf(vehicle);
    if (!keys.length || !variant) return 'unknown';
    const override = list(variant.vehicles);
    const values = override.length ? override : list(product.vehicles);
    if (values.length) return keys.some((key) => values.includes(key)) ? 'match' : 'unlisted';
    /* No vehicle list. If the product's series:* tags leave out the vehicle's series it is not listed for it;
       series tags never produce a positive match. */
    const series = list(product.series);
    const vehicleSeries = typeof vehicle === 'object' ? vehicle.series : null;
    if (series.length && vehicleSeries && !series.includes(vehicleSeries)) return 'unlisted';
    return 'unknown';
  }
  function summarize(product, vehicle) {
    if (!vehicle) return 'choose';
    const variants = product.variants || [];
    const states = variants.map((variant) => compatible(product, variant, vehicle));
    const complete = variants.length > 0 && variants.length === Number(product.variantCount);
    if (complete && states.every((state) => state === 'match')) return 'match';
    if (states.includes('match')) return 'options';
    if (!complete || states.includes('unknown')) return 'unknown';
    return 'unlisted';
  }
  function frontPair(variant) {
    return (variant?.options || []).some((option) => /^front(?:s| pair| only| pair only)$/i.test(option.trim()));
  }
  function rearOption(source, selected, addon, vehicle) {
    if (!frontPair(selected) || compatible(source, selected, vehicle) !== 'match') return null;
    return (addon.variants || []).find((variant) => variant.available && compatible(addon, variant, vehicle) === 'match') || null;
  }
  /* Short lines. "your" only for the remembered vehicle; a vehicle that is just on show (a link, a hub page) is named
     without it. Listing cards say nothing until a vehicle is on show. */
  function message(state, vehicle, mine, mode) {
    const name = vehicle ? (mine ? 'your ' : '') + vehicle.label : '';
    if (mode === 'card') {
      return state === 'match' ? 'Fits ' + name :
        state === 'options' ? 'Options fit ' + name :
        state === 'unlisted' ? 'Not listed for ' + name :
        state === 'unknown' ? 'Fitment not listed yet' : '';
    }
    return state === 'match' ? 'Fits ' + name :
      state === 'unlisted' ? 'This option is not listed for ' + name :
      state === 'unknown' ? 'Fitment for ' + name + ' is not listed yet' :
      state === 'pending' ? 'Checking selected option…' :
      'Choose your Falcon to check the fit.';
  }
  /* r10: a coilover Rear pair or Full set (sedans only) picked while a ute or wagon is on show. The product page fit line
     then says so in one short line, without the "Fitment guide" link, so the line keeps its height and the choice
     below it does not move when the option is tapped (the line under Add to cart explains, falcx-config.js). */
  function sedanOnlyOption(variant) {
    return (variant?.options || []).some((option) => /^(rear pair|full set)$/i.test(String(option).trim()));
  }
  /* r2 follow-up (13 Oct 2026): only when the product lists the vehicle's series, i.e. the sedan of that series is not
     unlisted for this option (`sedan` from the vehicle data). A BA–BF ute on the FG–FGX page is the wrong series, so it
     keeps the plain "not listed" line. Without `sedan` (no vehicle data) the old rule applies. */
  function sedanOnlyPick(state, variant, vehicle, product, sedan) {
    if (!(state === 'unlisted' && !!vehicle && typeof vehicle === 'object' && !!vehicle.body && vehicle.body !== 'sedan' && sedanOnlyOption(variant))) return false;
    return !(product && sedan) || compatible(product, variant, sedan) !== 'unlisted';
  }
  /* Listing order: fits first, then options, then not known, then not listed. */
  const RANK = { match: 0, options: 1, unknown: 2, choose: 2, pending: 2, unlisted: 3 };
  function rank(state) { return RANK[state] ?? 2; }
  /* Stable order of [{rank, index}] items: by rank, then by original position. */
  function order(items) { return [...items].sort((x, y) => x.rank - y.rank || x.index - y.index); }
  const core = { compatible, summarize, frontPair, rearOption, message, rank, order, sedanOnlyOption, sedanOnlyPick };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || window.FalcxFitment) return;
  window.FalcxFitment = core;
  const selected = new Map();
  const generations = new Map();
  const cache = new WeakMap();
  let serial = 0;
  function unitOf(card) {
    if (card.matches('.falcx-pcard')) return card;
    return card.closest('.product-grid__item, .resource-list__item');
  }
  /* Move fitting products to the front of their own list. Stable, and back to the original order when no vehicle is
     on show. Nothing is removed. */
  function reorder(cards) {
    const groups = new Map();
    cards.forEach(({ element, state }) => {
      const unit = unitOf(element);
      if (!unit || !unit.parentNode) return;
      if (!unit.dataset.fxI) unit.dataset.fxI = String(++serial);
      const value = rank(state);
      if (!groups.has(unit.parentNode)) groups.set(unit.parentNode, new Map());
      const units = groups.get(unit.parentNode);
      units.set(unit, Math.min(value, units.has(unit) ? units.get(unit) : 9));
    });
    groups.forEach((units, parent) => {
      const current = [...parent.children].filter((child) => units.has(child));
      const wanted = order(current.map((unit) => ({ unit, rank: units.get(unit), index: Number(unit.dataset.fxI) }))).map((item) => item.unit);
      if (wanted.every((unit, i) => unit === current[i])) return;
      const after = current[current.length - 1].nextSibling;
      wanted.forEach((unit) => parent.insertBefore(unit, after));
    });
  }
  function data(element, attribute = 'fitment') {
    const raw = element.dataset[attribute];
    const existing = cache.get(element) || {};
    if (existing[attribute]?.raw === raw) return existing[attribute].value;
    let value = {};
    try {
      const parsed = JSON.parse(raw || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) value = parsed;
    } catch (_) {}
    existing[attribute] = { raw, value };
    cache.set(element, existing);
    return value;
  }
  function scopeOf(element) {
    return element.closest('.shopify-section') || document;
  }
  function variantFor(element, product) {
    const stored = selected.get(String(product.productId));
    if (stored?.pending) return null;
    const forms = scopeOf(element).querySelectorAll('product-form-component');
    let nativeInput;
    for (const form of forms) {
      if (String(form.dataset.productId) === String(product.productId)) {
        nativeInput = form.querySelector('input[name="id"]');
        if (nativeInput) break;
      }
    }
    if (stored && (!stored.id || (nativeInput && String(nativeInput.value) !== stored.id))) return null;
    const id = stored ? stored.id : nativeInput ? nativeInput.value : product.selectedVariantId;
    return (product.variants || []).find((variant) => String(variant.id) === String(id));
  }
  function setText(element, value) {
    if (element && element.textContent !== value) element.textContent = value;
  }
  function update() {
    const api = window.FalcxVehicle;
    const vehicle = api ? (api.getActive ? api.getActive() : api.getSaved()) : null;
    const mine = !!(vehicle && api && (api.isMine ? api.isMine(vehicle) : true));
    const cards = [];
    document.querySelectorAll('[data-falcx-fitment]').forEach((element) => {
      const product = data(element);
      const mode = element.dataset.falcxFitment;
      const current = mode === 'card' ? null : variantFor(element, product);
      if (mode === 'addon') {
        const candidate = vehicle && rearOption(product, current, data(element, 'addonFitment'), vehicle);
        element.hidden = !candidate;
        if (candidate?.url) element.setAttribute('href', candidate.url);
        return;
      }
      const state = !vehicle ? 'choose' : mode === 'card' ? summarize(product, vehicle) :
        selected.get(String(product.productId))?.pending ? 'pending' : compatible(product, current, vehicle);
      const status = element.querySelector('[data-fitment-status]');
      const sedan = vehicle && typeof vehicle === 'object' && api && api.resolve ? api.resolve({ series: vehicle.series, body: 'sedan' }) : null;
      const sedanPick = mode === 'product' && sedanOnlyPick(state, current, vehicle, product, sedan);
      if (status) {
        if (status.dataset.state !== state) status.dataset.state = state;
        setText(status, sedanPick ? 'Sedans only, not for ' + (mine ? 'your ' : '') + vehicle.label : message(state, vehicle, mine, mode));
      }
      if (mode === 'card') {
        if (element.dataset.fit !== state) element.dataset.fit = state;
        cards.push({ element, state });
      }
      if (mode === 'product') {
        /* A product with one option has nothing to choose, so no "Selected option: Default Title" line. */
        setText(element.querySelector('[data-fitment-variant]'), !vehicle || Number(product.variantCount) === 1 ? '' : current ? 'Selected option: ' + current.title : 'Select a purchase option to check its fitment.');
        const choose = element.querySelector('[data-fitment-choose]');
        setText(choose, vehicle ? 'Change' : 'Choose your Falcon');
        if (choose) choose.setAttribute('aria-label', vehicle ? 'Change vehicle, now ' + vehicle.label : 'Choose your Falcon');
        /* Not a clear yes: offer the fitment guide beside the line. */
        const guide = element.querySelector('[data-fitment-guide]');
        if (guide) guide.hidden = sedanPick || !(state === 'unknown' || state === 'unlisted');
      }
    });
    reorder(cards);
    /* Lists were kept out of sight until sorted (falcx-head sets fx-sorting before first paint). */
    document.documentElement.classList.remove('fx-sorting');
  }
  function targetsForEvent(event) {
    const source = event.target;
    if (!(source instanceof Element) || source.closest('product-card, quick-add-dialog')) return [];
    return [...scopeOf(source).querySelectorAll('[data-falcx-fitment="product"]')];
  }
  /* r7: the product page choice (falcx-config.js) knows the exact variant a click selects, so the tick follows at once
     instead of "Checking selected option…" until Horizon answers. Horizon's answer still has the last word. */
  function expect(productId, variantId) {
    const id = String(productId || '');
    if (!id || !variantId) return;
    generations.set(id, (generations.get(id) || 0) + 1);
    selected.set(id, { id: String(variantId), pending: false, expected: true });
    update();
  }
  window.FalcxFitment.expect = expect;
  function markPending(id) {
    if (!selected.get(id)?.expected) selected.set(id, { pending: true });
  }
  async function onProductSelect(event) {
    const targets = targetsForEvent(event);
    const requests = targets.map((element) => {
      const id = String(data(element).productId);
      const generation = (generations.get(id) || 0) + 1;
      generations.set(id, generation);
      markPending(id);
      return { id, generation };
    });
    update();
    try {
      const result = await event.promise;
      for (const { id, generation } of requests) {
        if (generations.get(id) !== generation) continue;
        const detail = result?.detail;
        if (detail?.productId && String(detail.productId) !== id) {
          selected.set(id, { id: '', pending: false });
          continue;
        }
        selected.set(id, { id: String(detail?.resource?.id || ''), pending: false });
      }
    } catch (_) {
      for (const { id, generation } of requests) {
        if (generations.get(id) === generation) selected.set(id, { id: '', pending: false });
      }
    }
    update();
  }
  function init() {
    update();
    function afterResultsRender(event) {
      Promise.resolve(event.promise).then(update, update);
    }
    document.addEventListener('shopify:collection:update', afterResultsRender);
    document.addEventListener('shopify:search:update', afterResultsRender);
    document.addEventListener('falcx:vehicle-change', update);
    document.addEventListener('falcx:browsing-change', update);
    document.addEventListener('shopify:section:load', () => {
      selected.clear();
      generations.forEach((generation, id) => generations.set(id, generation + 1));
      update();
    });
    document.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('variant-picker')) {
        targetsForEvent(event).forEach((element) => markPending(String(data(element).productId)));
        update();
      } else if (event.target.matches('input[name="id"], select[name="id"]')) {
        targetsForEvent(event).forEach((element) => selected.delete(String(data(element).productId)));
        update();
      }
    });
    document.addEventListener('shopify:product:select', onProductSelect);
    let queued = false;
    new MutationObserver((records) => {
      const relevant = records.some((record) => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        return target?.closest('[data-falcx-fitment]') || [...record.addedNodes].some((node) =>
          node.nodeType === 1 && (node.matches('[data-falcx-fitment]') || node.querySelector('[data-falcx-fitment]')));
      });
      if (!relevant || queued) return;
      queued = true;
      queueMicrotask(() => { queued = false; update(); });
    }).observe(document.body, { childList: true, characterData: true, attributes: true, attributeFilter: ['data-fitment', 'data-addon-fitment'], subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
